import { Button, LockIcon, MailIcon, Paragraph, TextInput } from "@datagame/ui";
import { Formik, Form, Field } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { object, string } from "yup";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { SIGN_IN } from "../../../graphql/mutations/UserMutations";
import { useUserContext } from "../../../contexts/user/UserContext";
import { FieldWrapper } from "../../shared/ui/FieldWrapper";
import { ErrorMessage } from "../../shared/ui/ErrorMessage/ErrorMessage";

import styles from "./LoginForm.module.scss";

export interface LoginFormValues {
  email: string;
  password: string;
}

export const LoginForm = () => {
  const { t } = useTranslation("account");
  const { signIn } = useUserContext();
  const navigate = useNavigate();
  const [login, { error }] = useMutation(SIGN_IN, {
    onCompleted: ({ signIn: sigInData }) => {
      signIn(sigInData.accessToken);
      navigate("/", { replace: true });
    },
  });

  const initialValues: LoginFormValues = {
    email: "",
    password: "",
  };

  const handleSubmit = (values: LoginFormValues) => {
    login({
      variables: values,
    });
  };

  const validationSchema = object({
    email: string().email().required(),
    password: string().required(),
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ errors }) => (
        <Form className={styles.formWrapper}>
          {error && <ErrorMessage>{error.message}</ErrorMessage>}
          <div className={styles.fieldsWrapper}>
            <FieldWrapper label="Email address" name="email">
              <Field
                placeholder="Please enter your email..."
                name="email"
                as={TextInput}
                icon={<MailIcon />}
                error={errors.email}
              />
            </FieldWrapper>

            <FieldWrapper label="Password" name="password">
              <Field
                placeholder="Please enter your password..."
                type="password"
                name="password"
                as={TextInput}
                icon={<LockIcon />}
                error={errors.password}
              />
            </FieldWrapper>

            <Link to="/forgot-password" className={styles.link}>
              {t("forgotPassword.title")}
            </Link>
          </div>

          <Button className={styles.button} type="submit" size="large">
            {t("login")}
          </Button>
          <Paragraph color="secondary">
            {t("dontHaveAccount")}
            <Link to="/register" className={styles.link}>
              {t("signUp")}
            </Link>
          </Paragraph>
        </Form>
      )}
    </Formik>
  );
};
